import { DEFAULT_CURRENCY } from "@/money/currency_constants";
import numeral from "numeral";

export function formatNumberWithSpaces(num: number) {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

// The registry lives on the numeral singleton and register() throws on a duplicate name,
// so guard it: this module may re-execute in tests after a module-registry reset.
if (typeof numeral !== "undefined" && !numeral.formats["spaceseparated"]) {
  // Register a custom format
  numeral.register("format", "spaceSeparated", {
    regexps: {
      format: /(,)/,
      unformat: /\s/g,
    },
    format: function (value) {
      return formatNumberWithSpaces(value);
    },
    unformat: function (string) {
      return parseFloat(string.replace(/\s/g, ""));
    },
  });
}

// NOTE: groups digits by string surgery rather than toLocaleString() so the backend's
// exact decimal digits and trailing zeros survive without a float round-trip. Unlike
// formatNumberWithSpaces above, the separator is a non-breaking space so grouped
// amounts never wrap mid-number. formatNumberWithSpaces is not reusable here:
// its regex mis-groups the fraction.
export function formatMoneyString(value: string): string {
  const [integerPart, fractionPart] = value.split(".");
  const sign = integerPart.startsWith("-") ? "-" : "";
  const digits = sign ? integerPart.slice(1) : integerPart;
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, "\u00A0");
  return fractionPart === undefined
    ? `${sign}${grouped}`
    : `${sign}${grouped}.${fractionPart}`;
}

export function formatDate(dateString: string | null | undefined): string {
  if (!dateString) {
    return "";
  }

  const [year, month, day] = dateString.split("-");
  if (!day) {
    return `${month}/${year}`;
  }
  return `${day}/${month}/${year}`;
}

export function formatDateTimeIntoDate(
  dateString: string | null | undefined | Date
): string {
  if (!dateString) {
    return ""; // Return empty string if no date
  }
  // Parse the date string into a Date object if it is not already a Date object
  const date = dateString instanceof Date ? dateString : new Date(dateString);

  // Extract the day, month, and year from the Date object
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const year = date.getFullYear();

  // Format the date as DD/MM/YYYY
  return `${day}/${month}/${year}`;
}

// Wire-format counterpart to formatDateTimeIntoDate: returns YYYY-MM-DD built from
// the Date's *local* components. Use when posting a calendar date — avoids the
// `toISOString()` UTC shift that drops a day for any TZ east of UTC.
export function formatDateTimeIntoIsoDate(
  dateString: string | null | undefined | Date
): string {
  if (!dateString) {
    return "";
  }
  const date = dateString instanceof Date ? dateString : new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function formatMoney(value: number, precision = 2) {
  if (value === null || value === undefined) {
    return "n/a";
  }
  const roundedValue = Number(Number(value).toFixed(precision));
  const fmt = precision > 0 ? `0,0.${"0".repeat(precision)}` : "0,0";
  return numeral(roundedValue).format(fmt);
}

/** Formats a price as "€1,234.56" — always two decimals, thousands-separated. */
export function formatEuro(value: number): string {
  return `€${formatMoney(value)}`;
}

/** "€" for EUR, as EUR surfaces always printed it; the ISO code for every other currency. */
export function currencyLabel(currency: string): string {
  return currency === DEFAULT_CURRENCY ? "€" : currency;
}

/** Formats an amount followed by its currency label: "1,234.50 €", "310.12 GBP". */
export function formatMoneyWithCurrency(
  value: number,
  currency: string
): string {
  return `${formatMoney(value)} ${currencyLabel(currency)}`;
}

/**
 * "April 2027". A calendar date (`YYYY-MM` or `YYYY-MM-DD`) names its own month, so it is read in
 * UTC — `new Date` parses it as UTC midnight, which west of UTC is still the previous month locally.
 * A timestamp is shown in the viewer's timezone.
 *
 * @param locale - Defaults to the browser's; pass the interface language where the page has one.
 */
export function formatMonthYear(value: string, locale = "default"): string {
  if (!value) {
    return "";
  }
  const calendarDate = /^\d{4}-\d{2}(-\d{2})?$/.test(value);
  return new Date(value).toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    ...(calendarDate ? { timeZone: "UTC" } : {}),
  });
}

/**
 * Formats a date to "MMM YYYY" format (e.g., "Nov 2025").
 */
export function formatShortMonthYear(value: string | Date): string {
  if (!value) {
    return "";
  }
  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleDateString("default", {
    year: "numeric",
    month: "short",
  });
}

export function formatLargeNumber(value: number | null | undefined) {
  if (value === null || value === undefined) {
    return "n/a"; // Return a default text if value is null or undefined
  }
  return numeral(value).format("0,0");
}

export function formatMwh(value: number | null | undefined) {
  if (value === null || value === undefined) {
    return "n/a";
  }
  return formatLargeNumber(Math.round(value));
}

export function formatDateTime(
  dateInput: string | Date | null | undefined,
  locale: string | undefined = undefined
): string {
  /*
   * Format a date (or date string) into a localized string including time (HH:MM).
   * Falls back to empty string when input is null/undefined.
   */
  if (!dateInput) {
    return "";
  }

  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);

  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

/**
 * Checks if a date range represents a full calendar year (Jan 1 to Dec 31)
 */
export function isFullYearRange(startDate: Date, endDate: Date): boolean {
  const startYear = startDate.getUTCFullYear();
  const endYear = endDate.getUTCFullYear();

  return (
    startYear === endYear &&
    startDate.getUTCMonth() === 0 &&
    startDate.getUTCDate() === 1 &&
    endDate.getUTCMonth() === 11 &&
    endDate.getUTCDate() === 31
  );
}

/**
 * Formats a date as YYYY-MM-DD using UTC values
 */
export function formatDateCompactUTC(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")}`;
}
